import {useState, useEffect} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {requireHexCode} from '@/products/flexible-web-toolkit/colors'
import {Button as CButton} from '@/products/flexible-web-toolkit/components/button/button'
import type {ExtendedBlock} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import type {CtaButtonContentItem} from '@/products/flexible-web-toolkit/blocks/cta-button'
import type {ImageContentItem} from '../../flexible-web-toolkit/blocks/image'
import {ImageBlock} from '../../flexible-web-toolkit/blocks/image'

export type SiteBannerSelfValidationContentItem = IContentItem<{
	title: Elements.TextElement
	content: Elements.RichTextElement
	buttons: Elements.LinkedItemsElement<CtaButtonContentItem>
	popupSuppress: Elements.NumberElement
	selfValidationBannerTextalignment: Elements.MultipleChoiceElement
	selfValidationPopupSize: Elements.MultipleChoiceElement
	siteBannerImageComponent: Elements.LinkedItemsElement<ImageContentItem>
	siteBannerImageAlignment: Elements.MultipleChoiceElement
}>

const PANEL_SIZE_CLASSES = {
	large: 'max-h-[662px] max-w-7xl',
	medium: 'max-h-[495px] max-w-4xl',
	small: 'max-h-[331px] max-w-2xl',
} as const

// Keep panel height inside the frame padding (safe-area + 700px breakpoint pt-20).
const PANEL_VIEWPORT_HEIGHT_CLASSES = [
	'h-[calc(100svh-max(1.25rem,env(safe-area-inset-top,0px))-max(1rem,env(safe-area-inset-bottom,0px)))]',
	'[@media(min-height:700px)]:h-[calc(100svh-5rem-max(1rem,env(safe-area-inset-bottom,0px)))]',
].join(' ')

function getPanelSizeClass(bannerSize: string | undefined): string {
	if (bannerSize === 'large' || bannerSize === 'small') {
		return PANEL_SIZE_CLASSES[bannerSize]
	}

	return PANEL_SIZE_CLASSES.medium
}

function getImageAlignmentClasses(codename?: string): {
	containerClass: string
	innerClass: string
} {
	if (codename === 'left') {
		return {containerClass: 'block', innerClass: ''}
	}

	if (codename === 'right') {
		return {
			containerClass: 'flex justify-end',
			innerClass: 'flex justify-end',
		}
	}

	return {
		containerClass: 'flex items-center justify-center',
		innerClass: 'flex justify-center',
	}
}

export const SiteBannerSelfValidationBlock: ExtendedBlock<
	SiteBannerSelfValidationContentItem,
	{onValidate?: () => void}
> = ({
	block: {
		elements: {
			title,
			content,
			buttons,
			popupSuppress,
			selfValidationBannerTextalignment: alignment,
			selfValidationPopupSize: popupSize,
			siteBannerImageComponent,
			siteBannerImageAlignment: imageAlign,
		},
	},
	onValidate,
	...context
}) => {
	const cookieName = 'bannerCookie'
	const [showBanner, setShowBanner] = useState(true)
	const bannerSize = popupSize[0]?.codename ?? 'medium'
	const isLeftAligned = alignment[0]?.codename === 'left'
	const {containerClass, innerClass} = getImageAlignmentClasses(
		imageAlign[0]?.codename
	)
	const panelGreyHex = requireHexCode('panel-grey')

	function setCookie(name: string, value: string, days: number): void {
		const cookieExpiryMilliseconds = days * 24 * 60 * 60 * 1000
		const date = new Date()
		date.setTime(date.getTime() + cookieExpiryMilliseconds)
		document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`
	}

	useEffect(() => {
		if (!showBanner) {
			return
		}

		const previousOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'

		return () => {
			// Restore the exact prior inline value (including '') so stylesheet
			// overflow is not permanently overridden after the banner closes.
			document.body.style.overflow = previousOverflow
		}
	}, [showBanner])

	const handleCtaButtons = (_choice: string, cookieExpiry: number): void => {
		setShowBanner(false)
		setCookie(cookieName, 'true', cookieExpiry)
		onValidate?.()
	}

	return (
		<div
			className={clsx(
				'fixed inset-0 z-[999] overflow-y-auto overflow-x-hidden overscroll-contain backdrop-blur-[5px] backdrop-filter',
				!showBanner && 'hidden'
			)}
			data-testid="site-banner-self-validation-overlay"
		>
			<div
				className="flex min-h-full w-full justify-center bg-[rgba(28,28,28,0.76)] px-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-[max(1.25rem,env(safe-area-inset-top,0px))] [@media(min-height:700px)]:pt-20"
				data-testid="site-banner-self-validation-frame"
			>
				<div
					className={clsx(
						'mx-auto my-0 flex min-h-0 w-full flex-col',
						PANEL_VIEWPORT_HEIGHT_CLASSES,
						getPanelSizeClass(bannerSize)
					)}
					data-testid="site-banner-self-validation-panel"
					style={{
						border: `thick solid ${panelGreyHex}`,
						backgroundColor: panelGreyHex,
						borderRadius: '10px',
					}}
				>
					<div
						className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain bg-panel-grey scrollbar-thin md:flex-none"
						data-testid="site-banner-self-validation-content"
					>
						<div className="pb-5 pt-[30px]">
							<div
								className={clsx(
									'pl-5 pr-5 text-dark-charcoal',
									isLeftAligned ? 'text-left' : 'text-center'
								)}
							>
								<p className="text-2xl font-extrabold leading-[1.18]">
									{title}
								</p>
							</div>
						</div>
						<div className="border-t-[1px] border-[lightgrey] pb-[15px] pt-[15px]">
							<RichTextRenderer
								className={clsx(
									'justify-left pl-5 pr-5',
									isLeftAligned
										? 'text-left'
										: 'text-center text-dark-charcoal'
								)}
								data-kontent-element-codename="content"
								element={content}
								{...context}
							/>
							{siteBannerImageComponent[0] ? (
								<div className={containerClass}>
									<div className={innerClass}>
										<ImageBlock
											block={siteBannerImageComponent[0]}
											key={
												siteBannerImageComponent[0]
													.system.id
											}
											{...context}
										/>
									</div>
								</div>
							) : null}
						</div>
					</div>
					<div
						className="mx-auto my-0 flex max-h-[min(45%,14rem)] min-h-0 w-full shrink-0 justify-center overflow-y-auto overscroll-contain bg-panel-grey pb-[10px] pt-[10px] xxs:flex-col xs:flex-col"
						data-testid="site-banner-self-validation-actions"
					>
						{buttons.map(
							({elements: {url, text}, system: {id}}) => {
								return (
									<CButton
										className="ml-2 mr-2 w-1/4 xxs:mb-2 xxs:ml-0 xxs:w-full xxs:last:mb-0 xs:mb-2 xs:ml-0 xs:w-full xs:last:mb-0"
										href={url}
										key={id}
										onClick={() => {
											handleCtaButtons(
												url,
												popupSuppress ?? 0
											)
										}}
									>
										{text}
									</CButton>
								)
							}
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
